import { Fragment, useEffect, useMemo, useState } from "react";

import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";
import { searchTypeLabel } from "../../../utils/searchLeadTerminology";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { SearchPaletteItemType } from "../Inputs/SearchPaletteTypes";
import SearchPaletteDialog from "../Inputs/SearchPaletteDialog";

export default function OmniSearch() {
  const { t } = useTranslation("common");
  const hasLeads = useFeatureFlag("Lead Management");
  const hasStock = useFeatureFlag("Parts Tracker");
  const isCallCentre = useFeatureFlag("Call Centre");

  const allowedLinkedItemTypes = useMemo<SearchPaletteItemType[]>(
    () =>
      (["Lead", "Vehicle", "Stock"] as SearchPaletteItemType[]).filter((type) => {
        if (type === "Lead") return hasLeads;
        if (type === "Stock") return hasStock;
        return true;
      }),
    [hasLeads, hasStock]
  );

  const [searchType, setSearchType] = useState<SearchPaletteItemType>(
    hasLeads ? "Lead" : "Vehicle"
  );
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Feature flags load asynchronously, so keep the selected type valid if the
  // current one becomes disallowed (e.g. defaulted to Lead before flags arrived).
  useEffect(() => {
    if (!allowedLinkedItemTypes.includes(searchType) && allowedLinkedItemTypes.length) {
      setSearchType(allowedLinkedItemTypes[0]);
    }
  }, [allowedLinkedItemTypes, searchType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchValue.trim()) {
      return;
    }

    // Hand off to the search palette so results (and their VAT/GLOBAL indicator)
    // are shown for the typed term and selected type.
    setIsSearching(true);
  };

  return (
    <>
      <div className="flex flex-1 items-center justify-end px-4">
        <form className="flex w-full max-w-lg" onSubmit={handleSearch}>
          <div className="relative flex flex-1 items-center">
            {/* Search Type Dropdown */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex h-10 items-center gap-x-1 rounded-l-md border border-r-0 border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                {t(searchTypeLabel(searchType, isCallCentre))}
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 mt-2 w-32 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {allowedLinkedItemTypes.map((type) => (
                      <Menu.Item key={type}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setSearchType(type)}
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "w-full px-4 py-2 text-left text-sm text-gray-700"
                            )}
                          >
                            {t(searchTypeLabel(type, isCallCentre))}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Search Input */}
            <div className="relative flex-1">
              <label htmlFor="search-field" className="sr-only">
                {t("Search")}
              </label>
              <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                <input
                  id="search-field"
                  className="block h-10 w-full border border-l-0 border-r-0 border-gray-300 py-2 pr-3 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={searchType === "Stock" ? t("Tag number") : t("VRM")}
                  type="search"
                  name="search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  disabled={isSearching}
                />
              </div>
            </div>

            {/* Search Button */}
            <button
              type="button"
              onClick={() => setIsSearching(true)}
              className={classNames(
                "flex h-10 items-center justify-center rounded-r-md border border-l-0 border-transparent px-3 py-2 text-sm font-medium leading-4 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500",
                "bg-primary-600 hover:bg-primary-700 disabled:bg-gray-500 disabled:hover:bg-gray-500 text-white disabled:cursor-not-allowed"
              )}
            >
              <span className="sr-only">{t("Search")}</span>
              <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>

      <SearchPaletteDialog
        open={isSearching}
        onClose={() => setIsSearching(false)}
        placeholder={t("Search...")}
        theme="light"
        type={searchType}
        allowedTypes={allowedLinkedItemTypes}
        searchOnly={true}
        searchTerm={searchValue}
        setSearchTerm={setSearchValue}
      />
    </>
  );
}
